export type FetcherArg<T = unknown> = {
  body?: T;
  noResponse?: boolean;
};

const withRetry = async <T>(
  operation: (retryCount: number) => Promise<T>,
  retryCount = 0,
): Promise<T> => {
  try {
    const response = await operation(retryCount);

    if (
      response instanceof Response &&
      response.status === 401 &&
      retryCount === 0
    ) {
      console.log("Got 401 response, refreshing token and retrying...");
      return withRetry(operation, 1);
    }

    return response;
  } catch (error) {
    throw error;
  }
};

export const baseFetch = async <T>(
  path: string,
  method: string,
  token: string,
  fetcherArg?: Omit<FetcherArg<T>, "token">,
  retryCount: number = 0,
): Promise<Response> => {
  if (retryCount > 1) {
    console.log(retryCount);
  }
  const url = `${import.meta.env.VITE_API_URL}/${path}`;
  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };

  return await fetch(url, {
    method,
    headers,
    ...(fetcherArg?.body && { body: JSON.stringify(fetcherArg.body) }),
  });
};

export const fetcherGet = async (
  path: string,
  { arg, token }: { arg: FetcherArg; token: string },
) => {
  return await withRetry(async (retryCount) => {
    const response = await baseFetch(path, "GET", token, arg, retryCount);
    return await response.json();
  });
};

export const fetcherPut = async (
  path: string,
  { arg }: { arg: FetcherArg },
) => {
  return await withRetry(async (retryCount) => {
    return baseFetch(path, "PUT", "", arg, retryCount);
  });
};

export const fetcherPost = async <TBody, TResponse>(
  path: string,
  { arg, token }: { arg: FetcherArg<TBody>; token: string },
): Promise<TResponse> => {
  return await withRetry(async (retryCount) => {
    const response = await baseFetch<TBody>(
      path,
      "POST",
      token,
      arg,
      retryCount,
    );
    if (arg.noResponse) {
      return response;
    }
    return await response.json();
  });
};

export const fetcherPatchWithResponse = async (
  path: string,
  { arg }: { arg: FetcherArg },
) => {
  return await withRetry(async (retryCount) => {
    const response = await baseFetch(path, "PATCH", "", arg, retryCount);
    return await response.json();
  });
};

export const fetcherPatch = async (
  path: string,
  { arg }: { arg: FetcherArg },
) => {
  return await withRetry(async (retryCount) => {
    return baseFetch(path, "PATCH", "", arg, retryCount);
  });
};

export const fetcherDelete = async (
  path: string,
  { arg }: { arg: FetcherArg },
) => {
  return await withRetry(async (retryCount) => {
    return baseFetch(path, "DELETE", "", arg, retryCount);
  });
};
